/**
 * drawSkeleton — dibuja el esqueleto de MediaPipe Pose sobre un canvas
 * 2D. Hand-rolled (no usa DrawingUtils de @mediapipe/tasks-vision, que
 * no soporta el mapping object-contain/cover que necesitamos).
 *
 * Estilo default: cyan brillante (~#22D3EE) — alto contraste sobre el
 * feed de cámara (el brand teal-oscuro se pierde). Joints con stroke
 * blanco para contraste sobre cualquier fondo. Face landmarks (0-10)
 * con radio reducido para que no dominen visualmente.
 *
 * Uso:
 *   const rect = getVisibleVideoRect({...});
 *   ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
 *   ctx.clearRect(0, 0, cssWidth, cssHeight);
 *   drawSkeleton({ ctx, keypoints, rect });
 */

import { PoseLandmarker } from "@mediapipe/tasks-vision";
import type { Keypoint } from "./types";
import type { Rect } from "./videoRect";

export interface DrawSkeletonStyle {
  connectionColor: string;
  connectionWidth: number;
  landmarkColor: string;
  landmarkStrokeColor: string;
  landmarkStrokeWidth: number;
  bodyRadius: number;
  faceRadius: number;
  /** Umbral de visibility para renderizar un landmark. Landmarks con
   *  visibility menor NO se dibujan; conexiones con al menos un endpoint
   *  no visible tampoco. */
  minVisibility: number;
}

/** Cyan brillante (tailwind cyan-400 ~ #22D3EE). Alto contraste sobre
 *  cualquier feed de cámara. Confirmado por Ale — el brand teal-oscuro
 *  desaparece sobre la imagen del video. */
export const DEFAULT_SKELETON_STYLE: DrawSkeletonStyle = {
  connectionColor: "rgba(34, 211, 238, 0.85)",
  connectionWidth: 3,
  landmarkColor: "rgb(34, 211, 238)",
  landmarkStrokeColor: "rgba(255, 255, 255, 0.9)",
  landmarkStrokeWidth: 1.5,
  bodyRadius: 5,
  faceRadius: 3,
  minVisibility: 0.5,
};

/** Índices 0-10 de MediaPipe Pose son face landmarks: NOSE (0), EYES
 *  INNER/OUTER (1-6), EARS (7-8), MOUTH (9-10). Los dibujamos igual
 *  (por el ejercicio de cuello que necesita orejas), pero con radio
 *  reducido para que no dominen visualmente sobre el body pose. */
const FACE_LANDMARK_INDICES = new Set([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]);

export interface DrawSkeletonInput {
  ctx: CanvasRenderingContext2D;
  keypoints: Keypoint[];
  /** Bounding box del video visible dentro del canvas — resultado de
   *  getVisibleVideoRect. Landmarks normalizados [0..1] se proyectan
   *  a pixels con: rect.x + kp.x * rect.w, rect.y + kp.y * rect.h. */
  rect: Rect;
  /** Overrides parciales del estilo default. */
  style?: Partial<DrawSkeletonStyle>;
}

export function drawSkeleton({
  ctx,
  keypoints,
  rect,
  style,
}: DrawSkeletonInput): void {
  const s = style ? { ...DEFAULT_SKELETON_STYLE, ...style } : DEFAULT_SKELETON_STYLE;

  const isVisible = (idx: number): boolean => {
    const kp = keypoints[idx];
    return !!kp && kp.visibility >= s.minVisibility;
  };

  // Connections primero (debajo de los landmarks para que los círculos
  // queden por encima de las líneas — más limpio visualmente).
  ctx.strokeStyle = s.connectionColor;
  ctx.lineWidth = s.connectionWidth;
  ctx.lineCap = "round";
  for (const conn of PoseLandmarker.POSE_CONNECTIONS) {
    if (!isVisible(conn.start) || !isVisible(conn.end)) continue;
    const a = keypoints[conn.start];
    const b = keypoints[conn.end];
    ctx.beginPath();
    ctx.moveTo(rect.x + a.x * rect.w, rect.y + a.y * rect.h);
    ctx.lineTo(rect.x + b.x * rect.w, rect.y + b.y * rect.h);
    ctx.stroke();
  }

  // Landmarks (círculos rellenos con stroke blanco).
  ctx.fillStyle = s.landmarkColor;
  ctx.strokeStyle = s.landmarkStrokeColor;
  ctx.lineWidth = s.landmarkStrokeWidth;
  for (let i = 0; i < keypoints.length; i++) {
    if (!isVisible(i)) continue;
    const kp = keypoints[i];
    const r = FACE_LANDMARK_INDICES.has(i) ? s.faceRadius : s.bodyRadius;
    const px = rect.x + kp.x * rect.w;
    const py = rect.y + kp.y * rect.h;
    ctx.beginPath();
    ctx.arc(px, py, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }
}
